/**
 * IndexedDB layer for Avida Care offline field-visits.
 *
 * Two stores:
 *   • visits  — snapshot of the field-visit doc keyed by visit_id.
 *               Same shape as the backend doc; written every autosave.
 *   • queue   — ordered queue of pending writes the page couldn't
 *               POST to the server. Replayed in FIFO order by
 *               sync-queue.js when connectivity returns.
 *
 * Schema is intentionally small and append-only. Resetting the DB
 * is safe — the worst case is that the rep loses unsynced drafts,
 * which the sync queue already protects against by mirroring writes
 * to localStorage too.
 */
import { openDB } from "idb";

const DB_NAME = "avida-field-visits";
const DB_VERSION = 1;

let _dbPromise = null;

function getDB() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB not available in this browser"));
  }
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("visits")) {
          const visitStore = db.createObjectStore("visits", { keyPath: "id" });
          visitStore.createIndex("by_status", "status");
          visitStore.createIndex("by_updated_at", "updated_at");
        }
        if (!db.objectStoreNames.contains("queue")) {
          const q = db.createObjectStore("queue", {
            keyPath: "queue_id",
            autoIncrement: true,
          });
          q.createIndex("by_visit", "visit_id");
          q.createIndex("by_kind", "kind");
        }
      },
    });
  }
  return _dbPromise;
}

// ── Visit cache ─────────────────────────────────────────────────
export async function cacheVisit(visit) {
  if (!visit?.id) return;
  try {
    const db = await getDB();
    await db.put("visits", visit);
  } catch {
    /* IDB not available — fall back is localStorage in caller */
  }
}

export async function readCachedVisit(visitId) {
  try {
    const db = await getDB();
    return (await db.get("visits", visitId)) || null;
  } catch {
    return null;
  }
}

export async function listCachedDrafts() {
  try {
    const db = await getDB();
    return await db.getAllFromIndex("visits", "by_status", "draft");
  } catch {
    return [];
  }
}

export async function deleteCachedVisit(visitId) {
  try {
    const db = await getDB();
    await db.delete("visits", visitId);
  } catch {/* */}
}

// ── Pending write queue ─────────────────────────────────────────
// kind: 'patch' | 'sign' | 'submit'
// payload: the exact body the API expects for that endpoint
export async function enqueueWrite({ visitId, kind, payload, meta }) {
  try {
    const db = await getDB();
    const entry = {
      visit_id: visitId,
      kind,
      payload,
      meta: meta || {},
      created_at: new Date().toISOString(),
      attempts: 0,
    };
    return await db.add("queue", entry);
  } catch {
    return null;
  }
}

export async function listQueue() {
  try {
    const db = await getDB();
    return await db.getAll("queue");
  } catch {
    return [];
  }
}

export async function removeFromQueue(queueId) {
  try {
    const db = await getDB();
    await db.delete("queue", queueId);
  } catch {/* */}
}

export async function bumpQueueAttempt(queueId, error, nextRetryAt) {
  try {
    const db = await getDB();
    const tx = db.transaction("queue", "readwrite");
    const row = await tx.store.get(queueId);
    if (row) {
      row.attempts = (row.attempts || 0) + 1;
      row.last_error = error ? String(error).slice(0, 240) : "";
      row.last_tried_at = new Date().toISOString();
      if (nextRetryAt) row.next_retry_at = nextRetryAt;
      else row.next_retry_at = null;
      await tx.store.put(row);
    }
    await tx.done;
  } catch {/* */}
}

export async function clearQueueForVisit(visitId) {
  try {
    const db = await getDB();
    const tx = db.transaction("queue", "readwrite");
    const idx = tx.store.index("by_visit");
    let cursor = await idx.openCursor(IDBKeyRange.only(visitId));
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  } catch {/* */}
}
